import * as React from 'react';
import {
    useEffect,
    useState,
    lazy,
    Suspense,
    useCallback,
    useMemo,
} from 'react';
import { DeviceType, Vertical, Subvertical } from '../../../shared/types/ast';
import { SearchSummary } from '../../../shared/types/adsense';
import { ErrorBoundary } from '../error-boundary';
import { Placeholder } from '../placeholder';
import { Fallback } from '../fallback';
import { loadCompanionComponent } from '../../utils/component/load';

import {
    RESULT_LIST_SPACE_1,
    RESULT_LIST_SPACE_2,
    RESULT_LIST_SPACE_3,
    RESULT_LIST_ALT_SPACE_3,
    RESULT_LIST_SPACE_4,
    RESULT_LIST_ALT_SPACE_4,
    RESULT_LIST_SPACE_5,
    bannerName,
} from '../../utils/result';
import { getFeatureStatus } from '../../config';

const LazySearchResultBanners = lazy(() =>
    loadCompanionComponent('SearchResultBanners'),
);

export type SearchResultBannersComponent = React.FunctionComponent<Props>;

export type Props = {
    cols?: 1 | 2 | 3;
    vertical?: Vertical;
    subvertical?: Subvertical;
    deviceType?: DeviceType;
    searchSummary?: SearchSummary;
    children: React.ReactNode[];
};

const SearchResultBannersLoader: React.FC<Props> = (props) => {
    const { children } = props;

    return (
        <Suspense
            fallback={
                <SearchResultBannersServer {...props}>
                    {children}
                </SearchResultBannersServer>
            }
        >
            <LazySearchResultBanners {...props}>
                {children}
            </LazySearchResultBanners>
        </Suspense>
    );
};

const SearchResultBanners: React.FC<Props> = (props) => {
    const { children } = props;
    const [showBanners, setShowBanners] = useState(false);

    // Wait until after client-side hydration to show banners
    useEffect(() => {
        setShowBanners(true);
    }, []);

    return showBanners ? (
        <SearchResultBannersLoader {...props}>
            {children}
        </SearchResultBannersLoader>
    ) : (
        <SearchResultBannersServer {...props}>
            {children}
        </SearchResultBannersServer>
    );
};

type SpacingProps = {
    spacer: (id: string, index: number) => JSX.Element;
    cols?: 1 | 2 | 3;
    vertical?: Vertical;
    subvertical?: Subvertical;
    deviceType: DeviceType;
};

export const SearchResultBannersSpacing: React.FC<SpacingProps> = (props) => {
    const { spacer, deviceType, subvertical } = props;
    const altSpacingToggleEnabled = getFeatureStatus('enableAdIntervalVariant');
    const enableAdIntervalVariant =
        altSpacingToggleEnabled && deviceType === 'mobile';

    const adjustedSpaces = useMemo(() => {
        let space1 = RESULT_LIST_SPACE_1,
            space2 = RESULT_LIST_SPACE_2,
            space3 = RESULT_LIST_SPACE_3,
            space4 = RESULT_LIST_SPACE_4,
            space5 = RESULT_LIST_SPACE_5;

        if (enableAdIntervalVariant) {
            space3 = RESULT_LIST_ALT_SPACE_3;
            space4 = RESULT_LIST_ALT_SPACE_4;
        }
        // adjust for car rental widget
        if (subvertical === 'holiday_rental') {
            space2 += 1;
            space3 += 1;
            space4 += 1;
            space4 += 1;
        }
        return [space1, space2, space3, space4, space5];
    }, [enableAdIntervalVariant, subvertical]);

    const getChildren = useCallback(() => {
        return React.Children.map(props.children, (child, index) => {
            const indexInList = adjustedSpaces.indexOf(index);

            if (indexInList !== -1) {
                const id = indexInList + 1;
                return (
                    <>
                        {spacer(bannerName(deviceType, id), id)}
                        {child}
                    </>
                );
            }
            return child;
        });
    }, [adjustedSpaces, deviceType, spacer, props.children]);

    return <React.Fragment>{getChildren()}</React.Fragment>;
};

const SearchResultBannersServer: React.FC<Props> = (props) => {
    const { deviceType = 'desktop', cols, vertical, subvertical } = props;
    const children = React.Children.toArray(props.children);

    const spacer = useCallback(
        (id) => (
            <Placeholder id={id}>
                <Fallback id={id} deviceType={deviceType} />
            </Placeholder>
        ),
        [deviceType],
    );
    return (
        <SearchResultBannersSpacing
            deviceType={deviceType}
            cols={cols}
            vertical={vertical}
            subvertical={subvertical}
            spacer={spacer}
        >
            {children}
        </SearchResultBannersSpacing>
    );
};

const SearchResultBannersWithErrorBoundary: React.FC<Props> = (props) => {
    return (
        <ErrorBoundary fallback={props.children}>
            <SearchResultBanners {...props} />
        </ErrorBoundary>
    );
};

export default SearchResultBannersWithErrorBoundary;
